/**
 * Community Audit Performance Metrics Component
 *
 * Displays performance metrics as a histogram/bar chart with colors matching
 * the Performance Metrics Over Time chart legend.
 */

import { Shimmer } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import { ProgressBar } from '../../core/components';
import { getAuditMetricProgressColor, type AuditMetricType } from '../shared/constants/audit-metric-colors';

import * as styles from './CommunityAuditPerformanceMetrics.css';
import * as shimmerStyles from './shared/shimmer.css';

interface PerformanceMetrics {
  avgAccuracy?: number | null;
  avgPrecision?: number | null;
  avgRecall?: number | null;
  avgF1Score?: number | null;
}

interface ComprehensiveAnalytics {
  performanceMetrics: PerformanceMetrics;
}

interface CommunityAuditPerformanceMetricsProps {
  comprehensiveAnalytics?: ComprehensiveAnalytics | null;
  isLoading?: boolean;
}

// Helper function to format percentage and get bar width
function getBarData(value?: number | null): { percentage: string; width: number } {
  if (!value) return { percentage: 'N/A', width: 0 };
  const percent = Math.round(value * 100);

  return { percentage: `${percent}%`, width: percent };
}

// Helper function to render a metric bar with animation using shared constants
function renderMetricBar(options: { label: string; value: number | null | undefined; metricType: AuditMetricType; animate: boolean }) {
  const { label, value, metricType, animate } = options;
  const { percentage, width } = getBarData(value);

  // Get color variant using shared constants
  const color = getAuditMetricProgressColor(metricType);

  return (
    <div className={styles.barItem}>
      <div className={styles.barHeader}>
        <span className={styles.barLabel}>{label}</span>
        <span className={styles.barValue}>{percentage}</span>
      </div>
      <ProgressBar animated={animate} aria-label={`${label}: ${percentage}`} color={color} size="panel" value={animate ? width : 0} />
    </div>
  );
}

export function CommunityAuditPerformanceMetrics({ comprehensiveAnalytics, isLoading = false }: CommunityAuditPerformanceMetricsProps) {
  const [animate, setAnimate] = useState(false);

  // Trigger animation when data loads
  useEffect(() => {
    if (!isLoading && comprehensiveAnalytics) {
      // Small delay to ensure DOM is ready, then trigger animation
      const timer = setTimeout(() => setAnimate(true), 100);

      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
    }
  }, [isLoading, comprehensiveAnalytics]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </div>
        <div className={styles.content}>
          {['accuracy', 'precision', 'recall', 'f1'].map(metric => (
            <div key={`shimmer-${metric}`} className={styles.barItem}>
              <div className={styles.barHeader}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
              <Shimmer className={shimmerStyles.shimmerProgressBar} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // If no analytics data, show empty bars
  if (!comprehensiveAnalytics) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.title}>Audit Performance</span>
        </div>
        <div className={styles.content}>
          {renderMetricBar({ label: 'Accuracy', value: null, metricType: 'accuracy', animate: false })}
          {renderMetricBar({ label: 'Precision', value: null, metricType: 'precision', animate: false })}
          {renderMetricBar({ label: 'Recall', value: null, metricType: 'recall', animate: false })}
          {renderMetricBar({ label: 'F1 Score', value: null, metricType: 'f1Score', animate: false })}
        </div>
      </div>
    );
  }

  const { performanceMetrics } = comprehensiveAnalytics;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Audit Performance</span>
      </div>
      <div className={styles.content}>
        {renderMetricBar({ label: 'Accuracy', value: performanceMetrics.avgAccuracy, metricType: 'accuracy', animate })}
        {renderMetricBar({ label: 'Precision', value: performanceMetrics.avgPrecision, metricType: 'precision', animate })}
        {renderMetricBar({ label: 'Recall', value: performanceMetrics.avgRecall, metricType: 'recall', animate })}
        {renderMetricBar({ label: 'F1 Score', value: performanceMetrics.avgF1Score, metricType: 'f1Score', animate })}
      </div>
    </div>
  );
}
